import type {
  ListingTemplateImage,
  ListingTemplateProduct,
  ListingTemplateSpec,
  ListingTemplateVariant,
} from "./listing-templates";
import type { CanonicalPublicationSnapshot } from "./providers/canonical-publication-snapshot";
import { canonicalFromListingPayload } from "./providers/canonical-publication-snapshot";

/**
 * Safe adapter: canonical Tradivo product/listing/variant/media data →
 * `ListingTemplateProduct`.
 *
 * CANONICAL TEMPLATE-DATA CONTRACT
 * --------------------------------
 * Browser preview and server publication both map
 * `CanonicalPublicationSnapshot` through `listingTemplateProductFromSnapshot`.
 * Preview builds that snapshot with `canonicalFromListingPayload` so images,
 * dimensions, and channel/effective prices match publication.
 *
 * Fields that CANNOT exist in a publication snapshot — and are therefore
 * deliberately empty here (no fabrication) — are:
 *   shortDescription, keyFeatures, material, warranty, wasPrice,
 *   shipping/returns/support lines, rating.
 */

export interface CanonicalProductForTemplate {
  name?: string | null;
  brand?: string | null;
  description?: string | null;
  short_description?: string | null;
  sku?: string | null;
  selling_price?: number | null;
  condition?: string | null;
  weight_kg?: number | null;
  length_cm?: number | null;
  width_cm?: number | null;
  height_cm?: number | null;
  manufacturer?: string | null;
  model_number?: string | null;
  mpn?: string | null;
  product_type?: string | null;
}

export interface CanonicalListingForTemplate {
  channel_title?: string | null;
  channel_description?: string | null;
  channel_short_description?: string | null;
  channel_category_id?: string | null;
  currency?: string | null;
}

export interface CanonicalMediaForTemplate {
  url: string;
  alt?: string | null;
  public_url?: string | null;
  src?: string | null;
  image_url?: string | null;
}

export interface CanonicalVariantForTemplate {
  sku?: string | null;
  channel_sku?: string | null;
  option_values?: Record<string, string> | null;
  selling_price?: number | null;
  channel_price?: number | null;
  effective_channel_price?: number | null;
  variant?: {
    sku?: string | null;
    option_values?: Record<string, string> | null;
    selling_price?: number | null;
    weight_kg?: number | null;
    length_cm?: number | null;
    width_cm?: number | null;
    height_cm?: number | null;
  } | null;
}

export interface CanonicalTemplateInput {
  product: CanonicalProductForTemplate | null | undefined;
  listing: CanonicalListingForTemplate | null | undefined;
  media: CanonicalMediaForTemplate[];
  variants: CanonicalVariantForTemplate[];
  currency?: string | null;
  shippingLines?: string[];
  returnsLines?: string[];
  supportLines?: string[];
}

function asText(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function splitParagraphs(description: string): string[] {
  const trimmed = description.trim();
  if (!trimmed) return [];
  return trimmed
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);
}

function trimZeros(n: number): string {
  return Number(n.toFixed(2)).toString();
}

function formatPrice(currency: string | undefined, amount: number | undefined): string {
  if (amount == null || !Number.isFinite(Number(amount))) return "";
  const code = (currency ?? "").trim();
  return code ? `${code} ${Number(amount).toFixed(2)}` : `${Number(amount).toFixed(2)}`;
}

function dimensionsFrom(cm: { length: number; width: number; height: number } | null | undefined): string {
  if (!cm) return "";
  const parts = [cm.length, cm.width, cm.height].filter(
    (v): v is number => v != null && Number.isFinite(Number(v)),
  );
  return parts.length === 3 ? `${parts.join(" × ")} cm` : "";
}

function weightFromGrams(grams: number | null | undefined): string {
  if (grams == null || !Number.isFinite(Number(grams))) return "";
  const g = Number(grams);
  if (g <= 0) return "";
  if (g < 1000) return `${g} g`;
  return `${trimZeros(g / 1000)} kg`;
}

function variantsFromOptions(options: CanonicalPublicationSnapshot["options"]): ListingTemplateVariant[] {
  return (options ?? [])
    .filter((o) => o.name && o.values.length > 0)
    .map((o) => ({ name: o.name, options: o.values }));
}

function axisValue(options: CanonicalPublicationSnapshot["options"], ...names: string[]): string {
  for (const name of names) {
    const axis = (options ?? []).find((o) => o.name.toLowerCase() === name.toLowerCase());
    if (axis) return axis.values.join(", ");
  }
  return "";
}

/** Same mapping used by listing-template-render for publication HTML. */
export function listingTemplateProductFromSnapshot(
  snapshot: CanonicalPublicationSnapshot,
): ListingTemplateProduct {
  const p = snapshot.product;
  const variants = snapshot.variants ?? [];
  const images: ListingTemplateImage[] = (snapshot.images ?? [])
    .filter((u) => typeof u === "string" && u.trim())
    .map((u) => ({ src: u.trim(), alt: p.title || "" }));

  const title = asText(p.title);
  const brand = asText(p.brand);
  const fullDescription = splitParagraphs(asText(p.description));

  const firstVariant = variants[0];
  const sku = firstVariant?.sku ? asText(firstVariant.sku) : "";
  const price = formatPrice(firstVariant?.currency, firstVariant?.price);
  const condition = asText(p.condition) || "New";

  const templateVariants = variantsFromOptions(snapshot.options);
  const colour = axisValue(snapshot.options, "colour", "color");
  const size = axisValue(snapshot.options, "size");

  const dimensions = dimensionsFrom(p.dimensions_cm);
  const weight = weightFromGrams(p.weight_grams);

  const specifications: ListingTemplateSpec[] = [];
  const pushSpec = (label: string, value: string) => {
    if (value) specifications.push({ label, value });
  };
  pushSpec("Brand", brand);
  pushSpec("MPN", asText(p.mpn));
  pushSpec("Condition", condition);
  pushSpec("Category", asText(p.category));
  pushSpec("Dimensions", dimensions);
  pushSpec("Weight", weight);

  return {
    title,
    brand: brand || undefined,
    shortDescription: "",
    fullDescription,
    keyFeatures: [],
    images,
    sku,
    price,
    condition,
    variants: templateVariants,
    specifications,
    dimensions,
    weight,
    material: "",
    colour,
    size,
    warranty: undefined,
    shipping: { title: "Shipping", lines: [] },
    returns: { title: "Returns", lines: [] },
    support: { title: "Customer Support", lines: [] },
    rating: { score: 0, count: 0 },
  };
}

export function listingTemplateProductFromCanonical(
  input: CanonicalTemplateInput,
): ListingTemplateProduct {
  const snapshot = canonicalFromListingPayload({
    listing: input.listing ?? {},
    product: input.product ?? {},
    media: input.media ?? [],
    variants: input.variants ?? [],
    currency: input.currency ?? input.listing?.currency ?? null,
  });
  return listingTemplateProductFromSnapshot(snapshot);
}
